/**
 * Skyyrizon Cart — live AJAX cart updates.
 *
 * Uses Shopify's Section Rendering API to update only the cart section
 * without a full page reload:
 *   POST /cart/change.js?sections=main  (section key from templates/cart.json)
 *   POST /cart/add.js?sections=main     (for related-products add-to-cart)
 *
 * On response, replaces the cart section HTML in place and re-wires events.
 * Debounced so rapid +/- clicks get coalesced into a single network call.
 * Disables controls during in-flight requests, reverts on errors.
 */
(function () {
  'use strict';

  var SECTION_ID = 'main';                  // matches key in templates/cart.json
  var SECTION_ROOT_SELECTOR = '.sky-cart-page';
  var DEBOUNCE_MS = 280;

  var routes = (window.Shopify && window.Shopify.routes) || {};
  var changeUrl = (routes.cart_change_url || '/cart/change.js') + '?sections=' + SECTION_ID;
  var addUrl = (routes.cart_add_url || '/cart/add.js') + '?sections=' + SECTION_ID;

  // Pending updates queued per line index, so rapid +/- clicks merge
  var pendingQty = {};          // line → target qty
  var debounceTimer = null;
  var inFlight = false;
  var nextPendingAfterFlight = null;

  // ── Network helpers ───────────────────────────────────────────

  function postChange(line, quantity) {
    return fetch(changeUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = {};
        if (text) { try { data = JSON.parse(text); } catch (e) { data = { description: text }; } }
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  function postAdd(formData) {
    return fetch(addUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = {};
        if (text) { try { data = JSON.parse(text); } catch (e) { data = { description: text }; } }
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  // ── DOM replacement ──────────────────────────────────────────

  function replaceSection(sectionHtml) {
    if (!sectionHtml) return;
    // Shopify returns the full section wrapped in its id container, like:
    // <div id="shopify-section-main" class="shopify-section">...<section class="sky-cart-page">...</section>...</div>
    // We want to replace the .sky-cart-page in the live DOM with the one in the response.
    var parser = new DOMParser();
    var doc = parser.parseFromString(sectionHtml, 'text/html');
    var newRoot = doc.querySelector(SECTION_ROOT_SELECTOR);
    var currentRoot = document.querySelector(SECTION_ROOT_SELECTOR);
    if (!newRoot || !currentRoot) return;

    // Replace inner HTML — outer <section> element stays, all handlers re-init below
    currentRoot.innerHTML = newRoot.innerHTML;

    // Re-init event handlers on the new markup
    bindCartLines();
    bindRelatedAdds();

    // Update the header cart bubble
    refreshCartBubble();
  }

  function refreshCartBubble() {
    fetch((routes.root || '/') + 'cart.js', { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        bubbles.forEach(function (b) { b.textContent = cart.item_count; });
        var wrapper = document.querySelector('.cart-count-bubble');
        if (wrapper) wrapper.style.display = cart.item_count > 0 ? '' : 'none';
      })
      .catch(function () { /* non-fatal */ });
  }

  // ── Quantity debounced flush ─────────────────────────────────

  function scheduleFlush() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flush, DEBOUNCE_MS);
  }

  function flush() {
    if (inFlight || Object.keys(pendingQty).length === 0) return;

    // Take a snapshot of pending and clear (new changes during flight queue in nextPendingAfterFlight)
    var toSend = Object.assign({}, pendingQty);
    pendingQty = {};

    var lines = Object.keys(toSend).map(Number).sort(function (a, b) { return a - b; });
    if (lines.length === 0) return;

    // Shopify's /cart/change.js only changes one line per call. If multiple lines
    // changed, chain them. We always change the LAST line with our section request
    // so we get the full updated section HTML in one response.
    inFlight = true;
    showLoading(true);

    var chain = Promise.resolve();
    for (var i = 0; i < lines.length - 1; i++) {
      (function (line) {
        chain = chain.then(function () {
          return postChange(line, toSend[line]);
        });
      })(lines[i]);
    }
    // Last change includes the section HTML in response
    var lastLine = lines[lines.length - 1];
    chain = chain.then(function () { return postChange(lastLine, toSend[lastLine]); });

    chain
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION_ID]) {
          replaceSection(data.sections[SECTION_ID]);
        } else {
          // Response didn't include re-rendered section — fall back to full reload
          // so the UI reflects real cart state (e.g. removed item disappears).
          window.location.reload();
        }
      })
      .catch(function (err) {
        var msg = (err && err.description) || (err && err.message) || 'Could not update cart.';
        console.error('[sky-cart] change failed:', err);
        window.alert(msg);
        window.location.reload();
      })
      .then(function () {
        inFlight = false;
        showLoading(false);
        if (nextPendingAfterFlight) {
          Object.assign(pendingQty, nextPendingAfterFlight);
          nextPendingAfterFlight = null;
          scheduleFlush();
        } else if (Object.keys(pendingQty).length > 0) {
          scheduleFlush();
        }
      });
  }

  function queueQty(lineIndex, quantity) {
    if (inFlight) {
      if (!nextPendingAfterFlight) nextPendingAfterFlight = {};
      nextPendingAfterFlight[lineIndex] = Math.max(0, quantity);
      return;
    }
    pendingQty[lineIndex] = Math.max(0, quantity);
    scheduleFlush();
  }

  function showLoading(isLoading) {
    var grid = document.querySelector('.sky-cart-page__grid');
    if (!grid) return;
    grid.style.opacity = isLoading ? '0.7' : '';
    grid.style.pointerEvents = isLoading ? 'none' : '';
  }

  // ── Line binding ─────────────────────────────────────────────

  function bindCartLines() {
    var lines = document.querySelectorAll('.sky-cart-line');
    lines.forEach(function (lineEl) {
      if (lineEl.dataset.skyBound === 'true') return;
      lineEl.dataset.skyBound = 'true';

      var input = lineEl.querySelector('[data-sky-qty-input]');
      var minus = lineEl.querySelector('[data-sky-qty-minus]');
      var plus = lineEl.querySelector('[data-sky-qty-plus]');
      var remove = lineEl.querySelector('[data-sky-cart-remove]');

      if (!input) return;
      var lineIndex = parseInt(input.getAttribute('data-index'), 10);

      if (minus) minus.addEventListener('click', function () {
        var current = parseInt(input.value, 10) || 0;
        var next = Math.max(0, current - 1);
        input.value = next;
        queueQty(lineIndex, next);
      });

      if (plus) plus.addEventListener('click', function () {
        var current = parseInt(input.value, 10) || 0;
        var next = current + 1;
        input.value = next;
        queueQty(lineIndex, next);
      });

      input.addEventListener('change', function () {
        var q = Math.max(0, parseInt(input.value, 10) || 0);
        input.value = q;
        queueQty(lineIndex, q);
      });

      if (remove) remove.addEventListener('click', function (e) {
        e.preventDefault();
        input.value = 0;
        queueQty(lineIndex, 0);
      });
    });
  }

  // ── Related products add-to-cart ─────────────────────────────

  function bindRelatedAdds() {
    var forms = document.querySelectorAll('[data-sky-cart-add-form]');
    forms.forEach(function (form) {
      if (form.dataset.skyBound === 'true') return;
      form.dataset.skyBound = 'true';

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (!btn || btn.disabled) return;
        var originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        showLoading(true);

        // Simple add (no sections needed — we reload after)
        var routes = (window.Shopify && window.Shopify.routes) || {};
        var addUrl = routes.cart_add_url || '/cart/add.js';
        var formData = new FormData(form);

        fetch(addUrl, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        }).then(function (res) {
          if (!res.ok) {
            return res.text().then(function (text) {
              var err = {};
              try { err = JSON.parse(text); } catch (e) { err = { description: text }; }
              throw err;
            });
          }
          // Success — page reload so the cart items grid above updates with the new line.
          // We use reload for related-adds because they introduce a new cart line
          // (the grid must re-render fully, not just mutate a counter).
          btn.textContent = 'Added ✓';
          window.location.reload();
        }).catch(function (err) {
          btn.disabled = false;
          btn.textContent = originalText;
          showLoading(false);
          var msg = (err && err.description) || (err && err.message) || 'Could not add.';
          window.alert(msg);
        });
      });
    });
  }

  // ── Init ─────────────────────────────────────────────────────

  function init() {
    bindCartLines();
    bindRelatedAdds();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
